import React from 'react';
import { toast } from 'react-toastify';
import {
  ApplicationStatus,
  ApplicationVendorQuestionnaireSteps,
  VendorQuestionnaireSteps,
} from '../../Lib/Constants/vendor-application-form-step';

import VendorInfo from './views/VendorInfo';
import FashionNovaApproval from './views/FashionNovaApproval';
import BusinessProfile from './views/BusinessProfile';
import RelationshipHistory from './views/RelationshipHistory';
import ComplianceAndQualityAssurance from './views/ComplianceAndQualityAssurance';
import FactoryProfiles from './views/FactoryProfiles';
import AdditionalCapabilities from './views/AdditionalCapabilities';
import BuyerDetails from './views/BuyerDetails';
import SponsorAndExecApproval from './views/SponsorAndExecApproval';
import Badge from '../ReusableComponents/NewDesign/Badge/Badge';

const stepComponents = {
  [ApplicationVendorQuestionnaireSteps.VENDOR_INFO]: VendorInfo,
  [ApplicationVendorQuestionnaireSteps.BUSINESS_PROFILE]: BusinessProfile,
  [ApplicationVendorQuestionnaireSteps.RELATIONSHIP_HISTORY]:
    RelationshipHistory,
  [ApplicationVendorQuestionnaireSteps.COMPLIANCE]:
    ComplianceAndQualityAssurance,
  [ApplicationVendorQuestionnaireSteps.FACTORY_PROFILES]: FactoryProfiles,
  [ApplicationVendorQuestionnaireSteps.ADDITIONAL_CAPABILITIES]:
    AdditionalCapabilities,
  [ApplicationVendorQuestionnaireSteps.FASHION_NOVA_APPROVAL]:
    FashionNovaApproval,
  [ApplicationVendorQuestionnaireSteps.BUYER_DETAILS]: BuyerDetails,
  [ApplicationVendorQuestionnaireSteps.SPONSOR_AND_EXEC_APPROVAL]:
    SponsorAndExecApproval,
};

const staticComponents = [
  ApplicationVendorQuestionnaireSteps.FASHION_NOVA_APPROVAL,
  ApplicationVendorQuestionnaireSteps.SPONSOR_AND_EXEC_APPROVAL,
];

const getComponentList = (stepList) =>
  stepList.map((step) => ({
    label: step,
    component: stepComponents[step],
    static: staticComponents.includes(step),
  }));

const getStepList = (merchType, role, applicationStatus) => {
  if (!VendorQuestionnaireSteps[merchType]) {
    toast(
      `Your MerchType is incorrect.
      Please, contact our technical support: ${role === 'INFLUENCER' ? 'influencerdocs@fashionnova.com' : 'vendormasterdata@fashionnova.com'}`,
      {
        type: 'error',
        autoClose: 7000,
      },
    );

    return [];
  }
  const steps = [...VendorQuestionnaireSteps[merchType]];

  if (applicationStatus === ApplicationStatus.VENDOR_QUESTIONNAIRE) {
    return getComponentList(steps);
  }

  if (role === 'VENDOR') {
    steps.push(ApplicationVendorQuestionnaireSteps.FASHION_NOVA_APPROVAL);
    return getComponentList(steps);
  }

  if (
    role === 'BUYER' ||
    role === 'SPONSOR' ||
    role === 'EXECUTIVE' ||
    role === 'ADMIN' ||
    role === 'PRODUCT_DEVELOPMENT'
  ) {
    steps.push(ApplicationVendorQuestionnaireSteps.BUYER_DETAILS);
    if (applicationStatus === ApplicationStatus.BUYER_QUESTIONNAIRE) {
      return getComponentList(steps);
    }
    steps.push(ApplicationVendorQuestionnaireSteps.SPONSOR_AND_EXEC_APPROVAL);
  }
  return getComponentList(steps);
};

const isLocked = (role, applicationStatus, step) => {
  if (role === 'VENDOR') {
    return applicationStatus !== ApplicationStatus.VENDOR_QUESTIONNAIRE;
  }

  if (role === 'BUYER') {
    if (applicationStatus === ApplicationStatus.BUYER_QUESTIONNAIRE) {
      return step !== ApplicationVendorQuestionnaireSteps.BUYER_DETAILS;
    }
  }

  if (role === 'PRODUCT_DEVELOPMENT') {
    if (applicationStatus === ApplicationStatus.BUYER_QUESTIONNAIRE) {
      return step !== ApplicationVendorQuestionnaireSteps.BUYER_DETAILS;
    }
  }
  return true;
};

const isEditable = (role, applicationStatus, displayedStep, activeStep) => {
  if (
    role === 'VENDOR' &&
    displayedStep !== activeStep &&
    displayedStep < activeStep
  ) {
    return applicationStatus === ApplicationStatus.VENDOR_QUESTIONNAIRE;
  }

  return false;
};

const isSponsorAprovable = (role, applicationStatus) =>
  role === 'SPONSOR' &&
  applicationStatus === ApplicationStatus.SPONSOR_APPROVAL;

const isViewMode = (activeStep, stepIndex, editMode, role) => {
  if (role === 'SPONSOR' || role === 'EXECUTIVE' || role === 'ADMIN') {
    return true;
  }
  return activeStep > stepIndex && !editMode;
};

const isFirstExecutiveApprovable = (role, applicationStatus) =>
  role === 'EXECUTIVE' &&
  applicationStatus === ApplicationStatus.FIRST_EXECUTIVE_APPROVAL;

const isSecondExecutiveApprovable = (
  role,
  applicationStatus,
  userId,
  metadata,
) =>
  role === 'EXECUTIVE' &&
  applicationStatus === ApplicationStatus.SECOND_EXECUTIVE_APPROVAL &&
  userId !== metadata.firstExecutiveApproval.executive_id;

const displayApprovalBadge = (role, applicationStatus) => {
  const executiveApprovals = {
    [ApplicationStatus.SECOND_EXECUTIVE_APPROVAL]: 'Executive 1 Approved',
    [ApplicationStatus.PRE_ONBOARDING]: 'Executive 2 Approved',
    [ApplicationStatus.FIRST_EXECUTIVE_DENIED]: 'Executive 1 Denied',
    [ApplicationStatus.SPONSOR_DENIED]: 'Sponsor Denied',
  };

  let label = '';
  let color = '';

  if (role === 'SPONSOR') {
    if (
      [
        ApplicationStatus.FIRST_EXECUTIVE_APPROVAL,
        ApplicationStatus.SECOND_EXECUTIVE_APPROVAL,
        ApplicationStatus.PRE_ONBOARDING,
      ].includes(applicationStatus)
    ) {
      label = 'Sponsor Approved';
      color = 'purple';
    } else if (applicationStatus === ApplicationStatus.SPONSOR_DENIED) {
      label = 'Sponsor Denied';
      color = 'red';
    }
  } else if (role === 'EXECUTIVE') {
    label = executiveApprovals[applicationStatus];
    if (label) {
      color = label.includes('Denied') ? 'red' : 'purple';
    }
  }

  return label ? <Badge color={color} label={label} size="medium" /> : null;
};

export {
  stepComponents,
  staticComponents,
  getStepList,
  getComponentList,
  isLocked,
  isEditable,
  isSponsorAprovable,
  isFirstExecutiveApprovable,
  isSecondExecutiveApprovable,
  isViewMode,
  displayApprovalBadge,
};
